import { apiClient } from './client';
import type { BaseProfile, ProfileStatus, ProfileDocuments, DocumentReviewStatus, IdType, ProfileName } from '@comp-sys/shared';

// Profiles API types
export interface CreateProfileRequest {
  targetUserId?: string; // For admin to create profile for another user
  names: ProfileName[];
  idType?: IdType;
  idNo?: string;
  nationality?: string;
  regionJson?: Record<string, unknown>;
  addressDetail?: string;
  photoUrl?: string;
  schoolId?: string;
  schoolName?: string;    // Manual school entry — creates school if schoolId not set
  schoolType?: string;     // School type for manual creation
  schoolRegionJson?: { province?: string; city?: string; district?: string };
  schoolAddress?: string;
  currentGrade?: string;
  documentsJson?: ProfileDocuments;
  status?: ProfileStatus;
  coachName?: string;
  coachPhone?: string;
  institutionId?: string;
  customFields?: Record<string, string>;
}

export interface UpdateProfileRequest {
  names?: ProfileName[];
  idType?: IdType;
  idNo?: string;
  nationality?: string;
  regionJson?: Record<string, unknown>;
  addressDetail?: string;
  photoUrl?: string;
  schoolId?: string;
  schoolName?: string;
  schoolType?: string;
  currentGrade?: string;
  schoolRegionJson?: { province?: string; city?: string; district?: string };
  schoolAddress?: string;
  certNo?: string;
  status?: ProfileStatus;
  documentsJson?: ProfileDocuments;
  coachName?: string;
  coachPhone?: string;
  customFields?: Record<string, string>;
  institutionId?: string;
  institutionName?: string;
}

export interface UpdateDocumentsRequest {
  idCard?: {
    frontUrl: string;
    backUrl: string;
  };
  passport?: {
    photoUrl: string;
  };
  otherId?: {
    frontUrl: string;
    backUrl?: string;
    type: string;
  };
}

export interface ReviewDocumentsRequest {
  status: DocumentReviewStatus;
  note?: string;
}

export interface ProfilesListResponse {
  data: BaseProfile[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface GenerateCertNoResponse {
  certNo: string;
}

export interface CanCreateResponse {
  canCreate: boolean;
  currentCount: number;
  maxAllowed: number | null;
}

export interface ProfilesListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED';
  schoolId?: string;
  role?: 'CONTESTANT' | 'JUDGE' | 'COACH' | 'VOLUNTEER';
}

export interface AdminProfilesListParams {
  role: 'CONTESTANT' | 'JUDGE' | 'COACH' | 'VOLUNTEER';
  status?: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED';
  schoolId?: string;
  competitionId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface AdminProfile extends BaseProfile {
  user: {
    id: string;
    email: string | null;
    phone: string | null;
    role: string;
  };
  school: {
    id: string;
    name: string;
  } | null;
}

export interface AdminProfilesListResponse {
  data: AdminProfile[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface GenerateProfileCertNoRequest {
  prefix?: string;
  separator?: string;
  paddingDigits?: number;
  startFrom?: number;
  includeYear?: boolean;
  role?: 'CONTESTANT' | 'JUDGE' | 'COACH' | 'VOLUNTEER' | 'ALL';
  schoolId?: string;
  status?: 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED' | 'ALL';
}

export interface GenerateProfileCertNoResponse {
  message: string;
  updated: number;
  prefix: string;
  separator: string;
  paddingDigits: number;
  startFrom: number;
  includeYear: boolean;
}

export interface ProfileCertNoStats {
  total: number;
  withoutCertNo: number;
  byRole: Record<string, { total: number; withoutCertNo: number }>;
}

export interface SetProfileCertNoRequest {
  certNo: string;
}

// Profiles API functions
export const profilesApi = {
  list: (params?: ProfilesListParams) => {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) {
      searchParams.set('page', String(params.page));
    }
    if (params?.limit !== undefined) {
      searchParams.set('limit', String(params.limit));
    }
    if (params?.search !== undefined) {
      searchParams.set('search', params.search);
    }
    if (params?.status !== undefined) {
      searchParams.set('status', params.status);
    }
    if (params?.schoolId !== undefined) {
      searchParams.set('schoolId', params.schoolId);
    }
    if (params?.role !== undefined) {
      searchParams.set('role', params.role);
    }
    const queryString = searchParams.toString();
    const endpoint = queryString ? `profiles?${queryString}` : 'profiles';
    return apiClient.get<ProfilesListResponse>(endpoint, { cache: 'no-store' });
  },

  get: (id: string) =>
    apiClient.get<BaseProfile>(`profiles/${id}`),

  create: (data: CreateProfileRequest) =>
    apiClient.post<BaseProfile>('profiles', data),

  createForUser: (targetUserId: string, data: Omit<CreateProfileRequest, 'targetUserId'>) =>
    apiClient.post<BaseProfile>('profiles', { ...data, targetUserId }),

  update: (id: string, data: UpdateProfileRequest) =>
    apiClient.put<BaseProfile>(`profiles/${id}`, data),

  reassignUser: (id: string, newUserId: string) =>
    apiClient.patch<BaseProfile>(`profiles/${id}/reassign-user`, { newUserId }),

  delete: (id: string) =>
    apiClient.delete<void>(`profiles/${id}`),

  generateCertNo: (id: string, prefix?: string) =>
    apiClient.post<GenerateCertNoResponse>(`profiles/${id}/generate-cert-no`, { prefix }),

  canCreate: (targetUserId?: string) => {
    const searchParams = new URLSearchParams();
    if (targetUserId) {
      searchParams.set('targetUserId', targetUserId);
    }
    const queryString = searchParams.toString();
    const endpoint = queryString ? `profiles/can-create?${queryString}` : 'profiles/can-create';
    return apiClient.get<CanCreateResponse>(endpoint);
  },

  getMyProfile: () =>
    apiClient.get<BaseProfile>('profiles/me'),

  getMyProfiles: (params?: { status?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) {
      searchParams.set('status', params.status);
    }
    const queryString = searchParams.toString();
    const endpoint = queryString ? `profiles/my-profiles?${queryString}` : 'profiles/my-profiles';
    return apiClient.get<BaseProfile[]>(endpoint);
  },

  getBySchool: async (schoolId: string): Promise<BaseProfile[]> => {
    return apiClient.get<BaseProfile[]>(`/profiles/school/${schoolId}`);
  },

  getBySchoolPaginated: async (schoolId: string, params?: { search?: string; page?: number; limit?: number }): Promise<{ data: BaseProfile[]; meta: { total: number; page: number; limit: number; totalPages: number } }> => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page !== undefined) searchParams.set('page', String(params.page));
    if (params?.limit !== undefined) searchParams.set('limit', String(params.limit));
    const qs = searchParams.toString();
    return apiClient.get(`/profiles/school/${schoolId}${qs ? `?${qs}` : ''}`);
  },

  getMySchoolProfiles: async (): Promise<BaseProfile[]> => {
    return apiClient.get<BaseProfile[]>('/profiles/my-school');
  },

  getMySchoolProfilesPaginated: async (params?: { search?: string; page?: number; limit?: number }): Promise<{ data: BaseProfile[]; meta: { total: number; page: number; limit: number; totalPages: number } }> => {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.set('search', params.search);
    if (params?.page !== undefined) searchParams.set('page', String(params.page));
    if (params?.limit !== undefined) searchParams.set('limit', String(params.limit));
    const qs = searchParams.toString();
    return apiClient.get(`/profiles/my-school${qs ? `?${qs}` : ''}`);
  },

  updateDocuments: (id: string, data: UpdateDocumentsRequest) =>
    apiClient.patch<BaseProfile>(`profiles/${id}/documents`, data),

  reviewDocuments: (id: string, data: ReviewDocumentsRequest) =>
    apiClient.post<BaseProfile>(`profiles/${id}/documents/review`, data),

  listForAdmin: (params: AdminProfilesListParams) => {
    const searchParams = new URLSearchParams();
    searchParams.set('role', params.role);
    if (params.status !== undefined) {
      searchParams.set('status', params.status);
    }
    if (params.schoolId !== undefined) {
      searchParams.set('schoolId', params.schoolId);
    }
    if (params.competitionId !== undefined) {
      searchParams.set('competitionId', params.competitionId);
    }
    if (params.search !== undefined) {
      searchParams.set('search', params.search);
    }
    if (params.page !== undefined) {
      searchParams.set('page', String(params.page));
    }
    if (params.limit !== undefined) {
      searchParams.set('limit', String(params.limit));
    }
    return apiClient.get<AdminProfilesListResponse>(`profiles/admin?${searchParams.toString()}`, { cache: 'no-store' });
  },

  // Batch generate certificate numbers
  generateCertNoBatch: (data: GenerateProfileCertNoRequest) =>
    apiClient.post<GenerateProfileCertNoResponse>('profiles/admin/generate-cert-no', data),

  // Get certificate number statistics
  getCertNoStats: (params?: { role?: string; schoolId?: string; status?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.role) searchParams.set('role', params.role);
    if (params?.schoolId) searchParams.set('schoolId', params.schoolId);
    if (params?.status) searchParams.set('status', params.status);
    const queryString = searchParams.toString();
    return apiClient.get<ProfileCertNoStats>(`profiles/admin/cert-no-stats${queryString ? `?${queryString}` : ''}`);
  },

  // Set certificate number manually
  setCertNo: (id: string, data: SetProfileCertNoRequest) =>
    apiClient.put<BaseProfile>(`profiles/${id}/cert-no`, data),

  getByUserId: (userId: string) =>
    apiClient.get<BaseProfile[]>(`profiles/user/${userId}`),

  batchDelete: async (
    data: { profileIds?: string[]; filter?: Record<string, string> }
  ): Promise<{ deletedCount: number }> => {
    return apiClient.delete<{ deletedCount: number }>('/profiles/batch', data);
  },

  // 档案审核相关
  getProfileIssues: (params?: {
    competitionId?: string;
    schoolId?: string;
    page?: number;
    limit?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params?.competitionId) searchParams.set('competitionId', params.competitionId);
    if (params?.schoolId) searchParams.set('schoolId', params.schoolId);
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const qs = searchParams.toString();
    return apiClient.get<{ data: BaseProfile[]; meta: { total: number; page: number; limit: number; totalPages: number } }>(`profiles/admin/issues${qs ? `?${qs}` : ''}`);
  },

  batchReject: (data: {
    profileIds: string[];
    reason?: string;
    issues?: Array<{ field: string; label: string; reason: string; autoDetected: boolean }>;
    notifyUsers?: boolean;
  }) => apiClient.post<{ rejectedCount: number }>('profiles/admin/batch-reject', data),

  detectIssues: (profileId: string) =>
    apiClient.post<Array<{ field: string; label: string; reason: string; autoDetected: boolean }>>(
      `profiles/${profileId}/detect-issues`,
      {},
    ),

  getMyWarnings: () =>
    apiClient.get<Array<{
      profileId: string;
      profileName: string;
      status: string;
      issues: Array<{ field: string; label: string; reason: string; autoDetected: boolean }>;
      enrolledCompetitions: string[];
    }>>('profiles/my-warnings'),
};
